import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideProblem } from './components/SlideProblem';
import { SlideAIOpportunity } from './components/SlideAIOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideAIEconomics } from './components/SlideAIEconomics';
import { SlideNewCategory } from './components/SlideNewCategory';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideSnakJourney } from './components/SlideSnakJourney';
import { SlidePlatformArchitecture } from './components/SlidePlatformArchitecture';
import { SlideMoat } from './components/SlideMoat';
import { SlideTraction } from './components/SlideTraction';
import { SlideSalesFocus } from './components/SlideSalesFocus';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideFinancialProjections } from './components/SlideFinancialProjections';
import { SlideTheRaise } from './components/SlideTheRaise';
import { SlideCTA } from './components/SlideCTA';
import { ExecutiveSummary } from './components/ExecutiveSummary';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const totalSlides = 19;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" />;
      case 1:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-1" />;
      case 2:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-2" />;
      case 3:
        return <SlideProblem onContinue={nextSlide} data-id="element-3" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-4" />;
      case 5:
        return <SlideAIEconomics onContinue={nextSlide} data-id="element-5" />;
      case 6:
        return <SlideNewCategory onContinue={nextSlide} data-id="element-6" />;
      case 7:
        return <SlideAIOpportunity onContinue={nextSlide} data-id="element-7" />;
      case 8:
        return <SlideStrategy onContinue={nextSlide} data-id="element-8" />;
      case 9:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-9" />;
      case 10:
        return <SlideSnakJourney onContinue={nextSlide} data-id="element-10" />;
      case 11:
        return <SlidePlatformArchitecture onContinue={nextSlide} data-id="element-11" />;
      case 12:
        return <SlideMoat onContinue={nextSlide} data-id="element-12" />;
      case 13:
        return <SlideTraction onContinue={nextSlide} data-id="element-13" />;
      case 14:
        return <SlideSalesFocus onContinue={nextSlide} data-id="element-14" />;
      case 15:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-15" />;
      case 16:
        return <SlideFinancialProjections onContinue={nextSlide} data-id="element-16" />;
      case 17:
        return <SlideTheRaise onContinue={nextSlide} data-id="element-17" />;
      case 18:
        return <SlideCTA onContinue={nextSlide} data-id="element-18" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-19">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-20" />

      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-21">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-22">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      <button onClick={() => setSummaryOpen(true)} className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" style={{
      color: '#ffffff'
    }} data-id="element-23">
        <Sparkles className="w-4 h-4" style={{
        color: '#ffffff'
      }} data-id="element-24" />
        <span style={{
        color: '#ffffff'
      }} data-id="element-25">
          Executive Summary
        </span>
      </button>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-26" />
    </div>;
}